import { Key, LogOut, Medal, User, Users, Users2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useGetProfile,
  useGetSession,
  useGetSubscriptions,
} from "../api/api.get";
import { capitalize } from "../utils/functions";
import { useAuthContext } from "../providers/auth.provider";
import { useMemo } from "react";
import { ENTITLEMENT } from "../utils/app";

export default function HomePage() {
  const { user } = useAuthContext();
  const { data: profile } = useGetProfile();
  const { data: userData } = useGetSession();

  const entitlementName = useMemo(() => {
    return user ? ENTITLEMENT[user.entitlement] ?? "TRIAL" : "TRIAL";
  }, [user?.entitlement]);

  return (
    <div id="home-body" className="max-w-5xl mx-auto min-h-screen">
      <div className="min-h-screen bg-gray-50 mt-0">
        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center mt-2 mb-16">
            <div className="">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Welcome back,{" "}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {capitalize(profile?.firstName) ?? "Guest"}!
                </span>
              </h1>
              <p className="text-lg text-gray-600">
                Manage all ExpenseHut settings from here
              </p>
            </div>

            <div className="flex justify-end mb-2">
              <Link to="/login?logout=1" className="flex gap-2 items-center">
                <LogOut className="w-4 h-4" />
                Logout
              </Link>
            </div>
          </div>

          {/* Welcome Section */}

          {/* Features Heading */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Quick Actions</h2>
          </div>

          {/* Tiles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer h-full">
              <div
                className={`w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
              >
                <Medal />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Subscription
              </h3>
              <div className="bg-gradient-to-r from-blue-700 to-purple-800 rounded-lg px-4 py-2 w-fit">
                <p className="text-2xl font-bold text-white">
                  {entitlementName}
                </p>
              </div>

              <div className="mt-4 flex items-center font-sm group-hover:gap-2 gap-0 transition-all">
                <span className="text-sm">
                  Email{" "}
                  <strong className="text-blue-700">
                    sales@expensehut.com
                  </strong>{" "}
                  to upgrade.
                </span>
              </div>
            </div>

            <Link to={"/settings/profile"} className="group">
              <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer h-full">
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <User />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Profile
                </h3>
                <p className="text-gray-600 text-sm">
                  Manage your personal information
                </p>
                <div className="mt-4 flex items-center text-blue-600 font-medium group-hover:gap-2 gap-0 transition-all">
                  <span>Go To</span>
                  <span className="text-lg">→</span>
                </div>
              </div>
            </Link>

            <Link to={"/settings/change-password"} className="group">
              <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer h-full">
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <Key />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Change Password
                </h3>
                <p className="text-gray-600 text-sm">
                  Update your account password
                </p>
                <div className="mt-4 flex items-center text-blue-600 font-medium group-hover:gap-2 gap-0 transition-all">
                  <span>Go To</span>
                  <span className="text-lg">→</span>
                </div>
              </div>
            </Link>

            {userData?.admin && (
              <Link to={"/admin/users"} className="group">
                <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer h-full">
                  <div
                    className={`w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <Users2 />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Manage Users
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Update user roles and permissions
                  </p>
                  <div className="mt-4 flex items-center text-blue-600 font-medium group-hover:gap-2 gap-0 transition-all">
                    <span>Go To</span>
                    <span className="text-lg">→</span>
                  </div>
                </div>
              </Link>
            )}

            {userData?.admin && (
              <Link to={"/admin/candidates"} className="group">
                <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer h-full">
                  <div
                    className={`w-12 h-12 rounded-lg bg-gradient-to-br from-black-500 to-black-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <Users2 />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Candidates
                  </h3>
                  <p className="text-gray-600 text-sm">See candidates</p>
                  <div className="mt-4 flex items-center text-blue-600 font-medium group-hover:gap-2 gap-0 transition-all">
                    <span>Go To</span>
                    <span className="text-lg">→</span>
                  </div>
                </div>
              </Link>
            )}
          </div>

          {/* Footer Section */}
          <div className="mt-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Need help?
                </h3>
                <p className="text-gray-600 mb-4 font-bold">
                  help@expensehut.com
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
